import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { usePackageGates } from '../../context/PackageGatesContext';
import {
  CheckCircle,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  Globe,
  Layers,
  Key,
  ArrowRight,
  MapPin,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { toast as notify } from 'sonner';
import PlatformIcons from '../../components/icons/PlatformIcons';
import {
  GOOGLE_SERVICE_PROVIDERS,
  buildIntegrationConnections,
  isProviderConnected,
} from './integrationsStatus';

// ─── Provider metadata (icons + descriptions) ────────────────────────────────

const PROVIDER_META = {
  bing_webmaster: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#00809D">
        <path d="M5 3v14.351l4.5 2.526L16 16.5l-3.5-1.504V7l-3 .847V4.5L5 3zm9.5 8.196L11 12.847l5 2.153-1.5-3.804z" />
      </svg>
    ),
    color: 'bg-[#00809D]/10 border-[#00809D]/30',
    badge: 'bg-[#00809D]',
    description: 'Submit URLs, view crawl stats & search queries for your Bing presence.',
  },
  ga4: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-6 h-6">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
    ),
    color: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800',
    badge: 'bg-blue-500',
    label: 'Google Analytics 4',
    description: 'Track website traffic and user behavior from your selected GA4 property.',
    kind: 'google_service',
  },
  gsc: {
    icon: () => <Search className="w-6 h-6 text-blue-500" />,
    color: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800',
    badge: 'bg-blue-500',
    label: 'Google Search Console',
    description: 'Monitor search performance and indexing from your selected Search Console site.',
    kind: 'google_service',
  },
  gbp: {
    icon: () => <MapPin className="w-6 h-6 text-emerald-500" />,
    color: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800',
    badge: 'bg-emerald-500',
    label: 'Google Business Profile',
    description: 'Manage your local business presence from your selected Business Profile location.',
    kind: 'google_service',
  },
  facebook: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1877F2">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    color: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800',
    badge: 'bg-[#1877F2]',
    description: 'Connect Facebook for supported Page publishing workflows.',
  },
  linkedin: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#0A66C2">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    color: 'bg-sky-50 border-sky-200 dark:bg-sky-950/20 dark:border-sky-800',
    badge: 'bg-[#0A66C2]',
    description: 'Share supported posts to your LinkedIn profile.',
  },
  twitter: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    color: 'bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700',
    badge: 'bg-black',
    description: 'Post single updates directly from GDS to your Twitter / X account.',
  },
  pinterest: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#E60023">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
      </svg>
    ),
    color: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800',
    badge: 'bg-[#E60023]',
    description: 'Connect Pinterest for supported pin workflows.',
  },
  youtube: {
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FF0000">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    color: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800',
    badge: 'bg-[#FF0000]',
    description: 'Analyze YouTube video SEO and generate optimized metadata suggestions.',
  },
  wordpress: {
    icon: () => <PlatformIcons.wordpress className="w-6 h-6" />,
    color: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800',
    badge: 'bg-[#21759B]',
    description: 'Manage pages, posts, and SEO metadata on your WordPress site.',
    kind: 'cms',
    label: 'WordPress',
  },
  shopify: {
    icon: () => <PlatformIcons.shopify className="w-6 h-6" />,
    color: 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800',
    badge: 'bg-[#96BF48]',
    description: 'Pull Shopify pages and blog articles for SEO review.',
    kind: 'cms',
    label: 'Shopify',
  },
  wix: {
    icon: () => <PlatformIcons.wix className="w-6 h-6" />,
    color: 'bg-purple-50 border-purple-200 dark:bg-purple-950/20 dark:border-purple-800',
    badge: 'bg-[#0C6EFC]',
    description: 'Sync Wix blog posts for SEO review.',
    kind: 'cms',
    label: 'Wix',
  },
  squarespace: {
    icon: () => <PlatformIcons.squarespace className="w-6 h-6" />,
    color: 'bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700',
    badge: 'bg-black',
    description: 'Read Squarespace pages and blog metadata for SEO review.',
    kind: 'cms',
    label: 'Squarespace',
  },
};

const PROVIDER_GROUPS = [
  {
    label: 'Google & Webmaster',
    icon: Search,
    providers: ['ga4', 'gsc', 'gbp', 'bing_webmaster'],
  },
  {
    label: 'Social Media',
    icon: Globe,
    providers: ['facebook', 'linkedin', 'twitter', 'pinterest', 'youtube'],
  },
  {
    label: 'Website & CMS',
    icon: Layers,
    providers: ['wordpress', 'shopify', 'wix', 'squarespace'],
  },
];

// Maps an OAuth provider to its backend feature gate key.
const PROVIDER_FEATURE = {
  ga4: 'google_analytics',
  gsc: 'google_search_console',
  gbp: 'google_business_page',
  bing_webmaster: 'bing_webmaster',
  facebook: 'facebook',
  linkedin: 'linkedin',
  twitter: 'twitter',
  pinterest: 'pinterest',
  youtube: 'youtube',
  wordpress: 'cms_wordpress',
  shopify: 'cms_shopify',
  wix: 'cms_wix',
  squarespace: 'cms_squarespace',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
  const { selectedProject } = useProject();
  const { canAccess } = usePackageGates();
  const tier = selectedProject?.package_type;

  const visibleGroups = PROVIDER_GROUPS
    .map(g => ({
      ...g,
      providers: g.providers.filter(p => {
        const feature = PROVIDER_FEATURE[p];
        return !feature || canAccess(feature, tier);
      }),
    }))
    .filter(g => g.providers.length > 0);

  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);   // provider being connected
  const [toast, setToast] = useState(null);
  const [apiKeyInputs, setApiKeyInputs] = useState({}); // per-provider API key drafts
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadConnections = useCallback(async () => {
    try {
      const [oauthResp, googleResp, cmsResp] = await Promise.all([
        api.get('/integrations/oauth/connections').catch(() => ({ data: {} })),
        api.get('/integrations/status').catch(() => ({ data: {} })),
        api.get('/cms/status').catch(() => ({ data: null })),
      ]);
      setConnections(buildIntegrationConnections({
        oauthConnections: oauthResp.data || {},
        googleStatus: googleResp.data || {},
        cmsStatus: cmsResp?.data,
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle redirect back from OAuth provider
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const email = searchParams.get('email');

    if (connected) {
      showToast(`${connected.replace('_', ' ')} connected successfully${email ? ` as ${email}` : ''}!`);
      setSearchParams({});
      loadConnections();
    } else if (error) {
      showToast(`Connection failed: ${error}`, 'error');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, loadConnections]);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  const handleConnect = async (provider) => {
    setConnecting(provider);
    try {
      if (GOOGLE_SERVICE_PROVIDERS.includes(provider)) {
        const redirectUri = window.location.origin + '/oauth/google/callback';
        const { data } = await api.get('/integrations/google/direct-auth-url', {
          params: { service: provider, redirect_uri: redirectUri, from_page: 'integrations' },
        });
        window.location.href = data.auth_url;
        return;
      }

      const { data } = await api.get(`/integrations/oauth/${provider}/authorize`);
      // Redirect the browser to the provider's consent page
      window.location.href = data.auth_url;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      showToast(`Could not start connection: ${detail}`, 'error');
      setConnecting(null);
    }
  };

  const handleApiKeyConnect = async (provider) => {
    const apiKey = (apiKeyInputs[provider] || '').trim();
    if (!apiKey) {
      showToast('Please enter an API key.', 'error');
      return;
    }
    setConnecting(provider);
    try {
      const { data } = await api.post(`/integrations/${provider}/connect`, { api_key: apiKey });
      setApiKeyInputs((prev) => ({ ...prev, [provider]: '' }));
      showToast(
        `${provider} connected successfully${data?.connected_email ? ` as ${data.connected_email}` : ''}!`,
      );
      loadConnections();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      showToast(`Could not connect ${provider}: ${detail}`, 'error');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    try {
      if (GOOGLE_SERVICE_PROVIDERS.includes(provider)) {
        await api.post('/integrations/google/disconnect', { service: provider });
      } else {
        await api.delete(`/integrations/oauth/${provider}/disconnect`);
      }
      setConnections((prev) => ({ ...prev, [provider]: { ...prev[provider], is_active: false } }));
      showToast(`${provider.replace('_', ' ')} disconnected.`, 'info');
    } catch (err) {
      showToast('Failed to disconnect.', 'error');
    }
  };

  const isConnected = (provider) => isProviderConnected(connections, provider);

  const PropertyPicker = ({
    label,
    items,
    selectedKey,
    onSelect,
    getKey,
    getLabel,
    getSubLabel,
    getBadge,
    saving,
    loading,
    error,
    onRetry,
    emptyText,
  }) => {
    const [expanded, setExpanded] = useState(!selectedKey);
    const [search, setSearch] = useState('');

    useEffect(() => {
      if (!selectedKey) setExpanded(true);
    }, [selectedKey]);

    if (loading) {
      return (
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mt-2 px-1">
          <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
          <span>Loading {label.toLowerCase()}…</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center gap-2 text-sm text-red-500 mt-2 px-1">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={onRetry} className="ml-1 underline hover:no-underline">Retry</button>
        </div>
      );
    }

    if (items.length === 0) {
      return <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 px-1">{emptyText}</p>;
    }

    const selectedItem = items.find(i => getKey(i) === selectedKey);
    const filtered = search
      ? items.filter(i => (
        getLabel(i).toLowerCase().includes(search.toLowerCase())
        || (getSubLabel && getSubLabel(i).toLowerCase().includes(search.toLowerCase()))
      ))
      : items;

    return (
      <div className="mt-3 space-y-2">
        {selectedItem && !expanded && (
          <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{getLabel(selectedItem)}</p>
                {getSubLabel && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{getSubLabel(selectedItem)}</p>
                )}
              </div>
              {getBadge && getBadge(selectedItem) && (
                <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                  {getBadge(selectedItem)}
                </span>
              )}
            </div>
            <button
              onClick={() => { setExpanded(true); setSearch(''); }}
              className="ml-3 flex-shrink-0 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 font-medium flex items-center gap-1"
            >
              Change <ChevronDown className="w-3 h-3" />
            </button>
          </div>
        )}

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Select {label}
                  </span>
                  {selectedItem && (
                    <button
                      onClick={() => { setExpanded(false); setSearch(''); }}
                      className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {items.length > 4 && (
                  <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Search className="w-3.5 h-3.5 flex-shrink-0" />
                      <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={`Search ${label.toLowerCase()}…`}
                        className="flex-1 text-sm bg-transparent outline-none text-slate-700 dark:text-slate-300 placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                )}

                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                  {filtered.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No results</p>
                  ) : filtered.map((item) => {
                    const key = getKey(item);
                    const selected = key === selectedKey;
                    return (
                      <button
                        key={key}
                        onClick={() => onSelect(item)}
                        disabled={saving}
                        className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 transition-colors group ${
                          selected
                            ? 'bg-indigo-50 dark:bg-indigo-950/50'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                        } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${selected ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-200'}`}>
                            {getLabel(item)}
                          </p>
                          {getSubLabel && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{getSubLabel(item)}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {saving && selected ? (
                            <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                          ) : selected ? (
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-400 dark:group-hover:text-slate-500" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const GA4PropertySelector = () => {
    const [properties, setProperties] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [selectorLoading, setSelectorLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchProperties = useCallback(async () => {
      setSelectorLoading(true);
      setError(null);
      try {
        const res = await api.get('/analytics/properties');
        setProperties(res.data.properties || []);
        setSelectedId(res.data.selected_property_id || null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load properties');
      } finally {
        setSelectorLoading(false);
      }
    }, []);

    useEffect(() => { fetchProperties(); }, [fetchProperties]);

    const handleSelect = async (item) => {
      setSaving(true);
      try {
        await api.post('/analytics/select-property', { property_id: item.property_id });
        setSelectedId(item.property_id);
        notify.success('GA4 property selected');
      } catch {
        notify.error('Failed to select property');
      } finally {
        setSaving(false);
      }
    };

    return (
      <PropertyPicker
        label="Property"
        items={properties}
        selectedKey={selectedId}
        onSelect={handleSelect}
        getKey={p => p.property_id}
        getLabel={p => p.display_name}
        getSubLabel={p => `${p.account_name} · ${p.property_id}`}
        saving={saving}
        loading={selectorLoading}
        error={error}
        onRetry={fetchProperties}
        emptyText="No GA4 properties found in this account."
      />
    );
  };

  const GSCSiteSelector = () => {
    const [sites, setSites] = useState([]);
    const [selectedUrl, setSelectedUrl] = useState(null);
    const [selectorLoading, setSelectorLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchSites = useCallback(async () => {
      setSelectorLoading(true);
      setError(null);
      try {
        const res = await api.get('/analytics/gsc/sites');
        setSites(res.data.sites || []);
        setSelectedUrl(res.data.selected_site_url || null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load sites');
      } finally {
        setSelectorLoading(false);
      }
    }, []);

    useEffect(() => { fetchSites(); }, [fetchSites]);

    const handleSelect = async (item) => {
      setSaving(true);
      try {
        await api.post('/analytics/gsc/select-site', { site_url: item.site_url });
        setSelectedUrl(item.site_url);
        notify.success('GSC site selected');
      } catch {
        notify.error('Failed to select site');
      } finally {
        setSaving(false);
      }
    };

    const permissionLabel = (level) => {
      if (!level) return null;
      const map = { siteOwner: 'Owner', siteFullUser: 'Full', siteRestrictedUser: 'Restricted', siteDelegatedUser: 'Delegated' };
      return map[level] || level;
    };

    return (
      <PropertyPicker
        label="Site"
        items={sites}
        selectedKey={selectedUrl}
        onSelect={handleSelect}
        getKey={s => s.site_url}
        getLabel={s => s.site_url.replace(/^(https?:\/\/)?(sc-domain:)?/, '')}
        getSubLabel={s => (s.site_url.startsWith('sc-domain:') ? 'Domain property' : 'URL prefix property')}
        getBadge={s => permissionLabel(s.permission_level)}
        saving={saving}
        loading={selectorLoading}
        error={error}
        onRetry={fetchSites}
        emptyText="No verified sites found in this Search Console account."
      />
    );
  };

  const GBPLocationSelector = () => {
    const [locations, setLocations] = useState([]);
    const [selectedName, setSelectedName] = useState(null);
    const [selectorLoading, setSelectorLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchLocations = useCallback(async () => {
      setSelectorLoading(true);
      setError(null);
      try {
        const res = await api.get('/gbp/list-locations');
        setLocations(res.data.locations || []);
        setSelectedName(res.data.selected_location_name || null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load locations');
      } finally {
        setSelectorLoading(false);
      }
    }, []);

    useEffect(() => { fetchLocations(); }, [fetchLocations]);

    const handleSelect = async (item) => {
      setSaving(true);
      try {
        await api.post('/gbp/select-location', { location_name: item.location_name, account_name: item.account_name });
        setSelectedName(item.location_name);
        notify.success('GBP location selected');
      } catch {
        notify.error('Failed to select location');
      } finally {
        setSaving(false);
      }
    };

    return (
      <PropertyPicker
        label="Business"
        items={locations}
        selectedKey={selectedName}
        onSelect={handleSelect}
        getKey={l => l.location_name}
        getLabel={l => l.title}
        getSubLabel={l => [l.account_display, l.address].filter(Boolean).join(' · ')}
        saving={saving}
        loading={selectorLoading}
        error={error}
        onRetry={fetchLocations}
        emptyText="No GBP locations found in this account."
      />
    );
  };

  const GoogleServiceSelector = ({ provider }) => {
    if (provider === 'ga4') return <GA4PropertySelector />;
    if (provider === 'gsc') return <GSCSiteSelector />;
    if (provider === 'gbp') return <GBPLocationSelector />;
    return null;
  };

  return (
    <motion.div
      className="space-y-8 p-4 md:p-6 max-w-5xl"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Connected Accounts</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            Connect your accounts once — GDS will use them automatically for all features.
          </p>
        </div>
        <button
          onClick={loadConnections}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`px-4 py-3 rounded-lg text-sm font-medium ${
              toast.type === 'error'
                ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-300'
                : toast.type === 'info'
                ? 'bg-slate-50 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-200'
                : 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
            }`}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Groups */}
      {loading ? (
        <div className="flex items-center gap-3 text-slate-500 text-sm py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading connections…
        </div>
      ) : (
        visibleGroups.map((group) => (
          <div key={group.label}>
            <div className="flex items-center gap-2 mb-4">
              <group.icon className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                {group.label}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.providers.map((provider) => {
                const meta = PROVIDER_META[provider];
                const conn = connections[provider];
                const connected = isConnected(provider);
                const isConnecting = connecting === provider;

                return (
                  <motion.div
                    key={provider}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`relative rounded-xl border p-5 flex flex-col gap-4 transition-shadow hover:shadow-md ${meta.color} bg-white dark:bg-slate-950`}
                  >
                    {/* Connected badge */}
                    {connected && (
                      <span className={`absolute top-3 right-3 text-[10px] font-semibold text-white px-2 py-0.5 rounded-full ${meta.badge}`}>
                        Connected
                      </span>
                    )}

                    {/* Icon + name */}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-900 shadow-sm border border-slate-100 dark:border-slate-800 flex items-center justify-center">
                        <meta.icon />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white text-sm">
                          {meta.label
                            || provider
                              .split('_')
                              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                              .join(' ')}
                        </p>
                        {connected && conn?.connected_email && (
                          <p className="text-xs text-slate-500 truncate max-w-[160px]">
                            {conn.connected_email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                      {meta.description}
                    </p>

                    {/* Action */}
                    {meta.kind === 'cms' ? (
                      <button
                        onClick={() => navigate('/dashboard/cms')}
                        className="mt-auto w-full flex items-center justify-center gap-2 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium py-2 rounded-lg transition-colors"
                      >
                        {connected ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                            Manage in CMS
                          </>
                        ) : (
                          <>
                            Manage in CMS
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    ) : connected ? (
                      <div className="flex items-center gap-2 mt-auto">
                        <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium flex-1">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Active
                        </div>
                        <button
                          onClick={() => handleDisconnect(provider)}
                          className="text-xs text-red-500 hover:text-red-700 hover:underline transition-colors"
                        >
                          Disconnect
                        </button>
                      </div>
                    ) : meta.authType === 'api_key' ? (
                      <div className="mt-auto flex flex-col gap-2">
                        <div className="relative">
                          <Key className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            type="password"
                            placeholder="Paste API key"
                            value={apiKeyInputs[provider] || ''}
                            onChange={(e) =>
                              setApiKeyInputs((prev) => ({ ...prev, [provider]: e.target.value }))
                            }
                            className="w-full pl-8 pr-2 py-1.5 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500"
                          />
                        </div>
                        <button
                          onClick={() => handleApiKeyConnect(provider)}
                          disabled={!!connecting}
                          className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                        >
                          {isConnecting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Verifying…
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Connect
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleConnect(provider)}
                        disabled={!!connecting}
                        className="mt-auto w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                      >
                        {isConnecting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Redirecting…
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-4 h-4" />
                            Connect
                          </>
                        )}
                      </button>
                    )}

                    {meta.kind === 'google_service' && connected && (
                      <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                          Active {provider === 'ga4' ? 'Property' : provider === 'gsc' ? 'Site' : 'Business'}
                        </p>
                        <GoogleServiceSelector provider={provider} />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Info box */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-5 text-sm text-slate-600 dark:text-slate-400 space-y-1">
        <p className="font-semibold text-slate-800 dark:text-slate-200">How it works</p>
        <p>Most account connections use OAuth — you log in with your provider account, approve access, and GDS stores a secure token. CMS connections may use provider-specific credentials or setup flows.</p>
        <p>Supported OAuth tokens auto-refresh in the background. If a connection expires, simply click Connect again.</p>
      </div>
    </motion.div>
  );
}
